import { motion } from "framer-motion";
import { OUTCOMES } from "@/lib/data";
import { fadeUp, inViewOnce, easeOutExpo } from "@/lib/motion";

export default function Manifesto() {
  return (
    <section
      id="program"
      data-testid="manifesto-section"
      className="relative py-24 md:py-40 border-t border-[color:var(--line)]"
    >
      <div className="mx-auto max-w-[1360px] px-5 md:px-8">
        {/* Section index */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={inViewOnce}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-between mb-14 md:mb-20"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-3)]">
            Ch. 01 — The case
          </span>
          <span className="font-mono text-[11px] tabular-nums text-[color:var(--ink-3)]">
            §001 / 010
          </span>
        </motion.div>

        <div className="grid grid-cols-12 gap-8 md:gap-16 items-start">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={inViewOnce}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.06 } },
            }}
            className="col-span-12 md:col-span-6 lg:col-span-7"
          >
            <motion.h2
              variants={fadeUp}
              className="font-serif text-[38px] sm:text-[52px] md:text-[68px] leading-[1] tracking-[-0.03em] text-[color:var(--ink)] text-balance"
            >
              Finance needs more than{" "}
              <em className="italic font-normal text-[color:var(--copper-2)]">
                another AI assistant.
              </em>
            </motion.h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={inViewOnce}
            transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.2 }}
            className="col-span-12 md:col-span-6 lg:col-span-5 md:pt-2"
          >
            <p className="text-[16px] md:text-[17px] leading-[1.65] text-[color:var(--ink-2)] text-pretty">
              AI already summarises reports, extracts information and helps
              analyse financial data. The next step is systems that coordinate
              tools, work across multiple data sources, execute structured
              workflows and evaluate their own outputs.
            </p>
            <p className="mt-5 text-[16px] md:text-[17px] leading-[1.65] text-[color:var(--ink-2)] text-pretty">
              This certification is how you build those systems responsibly for
              financial use cases.
            </p>
          </motion.div>
        </div>

        {/* Outcome cards */}
        <motion.ul
          initial="hidden"
          whileInView="show"
          viewport={inViewOnce}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
          }}
          className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--line-strong)] border border-[color:var(--line-strong)]"
        >
          {OUTCOMES.map((o) => (
            <motion.li
              key={o.n}
              variants={fadeUp}
              className="bg-[color:var(--bone)] p-6 md:p-8 flex flex-col justify-between min-h-[220px] group hover:bg-[color:var(--bone-2)] transition-colors duration-500"
              data-testid={`outcome-${o.title.toLowerCase()}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--ink-3)]">
                  {o.n}
                </span>
                <span className="w-6 h-px bg-[color:var(--copper)]" />
              </div>
              <div className="mt-10 md:mt-14">
                <h3 className="font-serif text-[30px] md:text-[36px] leading-none font-medium text-[color:var(--ink)]">
                  {o.title}
                </h3>
                <p className="mt-3 text-[14px] leading-[1.55] text-[color:var(--ink-2)]">
                  {o.body}
                </p>
              </div>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
